import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { Observable, of } from 'rxjs';
import { forkJoin } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { AlertService } from '@core/services/alert/alert.service';
import { ConfirmService } from '@core/services/confirm/confirm.service';
import { NavigationService } from '@core/services/navigation/navigation.service';
import { OnlineCreditService } from '@core/services/online-credit/online-credit.service';
import { StepManagerService } from '@core/services/online-credit/step-manager.service';
import { PrintDocumentService } from '@core/services/print/print-document.service';
import { FileUtilService } from '@core/services/utils/file-util.service';

import { LifecycleHookLogs } from '@shared/decorators/lifecycle-hook-logs/lifecycle-hook-logs.decorator';
import { FormFieldsListItem } from '@shared/models/modal-payment-form.model';

import { PersonalDataFormService } from '../personal-data-step/personal-data-form.service';
import { ActiveCreditGroupedByType } from '../store/store.model';
import { StoreService } from '../store/store.service';

@LifecycleHookLogs()
@UntilDestroy()
@Component({
    selector: 'app-confirmation-application-step',
    templateUrl: 'confirmation-application-step.page.html',
    styleUrls: ['./confirmation-application-step.page.scss', '../new-credit.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ConfirmationApplicationStepPage implements OnInit, OnDestroy {
  title: string = 'Проверка персональных данных';
  subtitle: string = '';

  public loaded$: Observable<boolean>;
  public userFormGroup: FormGroup;
  public userFormFiled: FormFieldsListItem[];
  public additionalFormGroup: FormGroup;
  public additionalFormField: any;
  public isFinalRequest: boolean;

  public agreeRules = {
    title: 'Подтверждаю достоверность сведений, ознакомлен и согласен с условиями',
    text: `<h4>Согласен с тем, что:</h4>
    <p>1. Все сведения, содержащиеся в настоящем Заявлении-анкете, а также все затребованные Банком документы предоставлены исключительно для получения Кредита, однако Банк оставляет за собой право использовать их как доказательство при судебном разбирательстве.<br>
    2. Банк оставляет за собой право проверки любой сообщаемой заемщиком о себе информации.<br>
    3. Предоставление заемщиком о себе ложной и вводящей в заблуждение информации может повлечь за собой отказ в предоставлении Кредита, а также гражданскую и уголовную (ст. 176 УК РФ) ответственность.</p>`
  };
  public agreeInsurance = {
    title: 'Согласен на уступку прав требования третьим лицам',
    text: `<p>Даю свое согласие на то, что право требования по заключенному между мной и Банком Договору потребительского кредита может быть уступлено Банком третьим лицам (в т.ч. некредитным организациям).</p>`
  };
  public agreeDebt = {
    title: 'Согласен на взыскание в бесспорном порядке',
    text: `<p>Даю свое согласие на то, что в случае возникновения задолженности, Банк вправе производить взыскание по заключенному между мной и Банком Договору потребительского кредита в бесспорном порядке на основании исполнительной надписи нотариуса в соответствии с действующим законодательством РФ.</p>`
  };

  public groupCredits$: Observable<ActiveCreditGroupedByType[]>;
  private timer;

  constructor(
    private stepManager: StepManagerService,
    private onlineCreditService: OnlineCreditService,
    private store: StoreService,
    private personalDataFormService: PersonalDataFormService,
    private confirmService: ConfirmService,
    private alertService: AlertService,
    private route: ActivatedRoute,
    private printDocumentService: PrintDocumentService,
    private fileUtilService: FileUtilService,
    private navigationService: NavigationService
  ) {
    this.loaded$ = this.stepManager.stepLoaded$;
  }

  get employerFormGroup(): FormGroup {
    return this.additionalFormGroup?.controls.employer as FormGroup;
  }

  get addressFormGroup(): FormGroup {
    return this.additionalFormGroup?.controls.address as FormGroup;
  }

  get prevDocFormGroup(): FormGroup {
    return this.additionalFormGroup?.controls.prevDoc as FormGroup;
  }

  get workExperienceFormGroup(): FormGroup {
    return this.additionalFormGroup?.controls.work as FormGroup;
  }

  get maritalFormGroup(): FormGroup {
    return this.additionalFormGroup?.controls.marital as FormGroup;
  }

  get otherFormGroup(): FormGroup {
    return this.additionalFormGroup?.controls.other as FormGroup;
  }

  get rulesFormGroup(): FormGroup {
    return this.additionalFormGroup?.controls.rules as FormGroup;
  }

  ngOnInit() {
    this.isFinalRequest = this.route.snapshot.queryParamMap.get('isFinalRequest') === 'true';
    if (!this.isFinalRequest) {
      this.initStepForFirstSending();
    } else {
      this.initStepForFinalSending();
    }
  }

  ionViewWillEnter() {}

  ngOnDestroy() {}

  ionViewWillLeave() {}

  confirm() {
    this.stepManager.setStepLoaded(false);
    if (!this.isFinalRequest) {
      this.confirmService
        .confirmDocument(this.store.value.application.id)
        .pipe(
          switchMap(() =>
            this.onlineCreditService.sendApplicationData({
              ...this.store.value.application,
              isSign: true
            })
          ),
          untilDestroyed(this)
        )
        .subscribe(
          (res) => {
            this.store.updateApplication(res);
            void this.navigationService.push('/new-product/credit/application-sending', { replaceUrl: true });
            // this.stepManager.changeStep(Steps.applicationSending);
          },
          (error) => {
            if (!error) {
              this.stepManager.showErrorStepAlert('Документ не был подписан');
            } else {
              const message = error.message || error.description || error || 'Неизвестная ошибка!';
              this.alertService.showInfoAlert(message);
              this.stepManager.setStepLoaded(true);
              // this.stepManager.showErrorStepAlert(err);
            }
          }
        );

      // this.onlineCreditService.sendApplicationData({...this.store.value.application, isSign: true }).pipe(
      // untilDestroyed(this)
      // ).subscribe((res) => {
      //   this.store.updateApplication(res);
      //   this.stepManager.changeStep(Steps.applicationSending);
      // }, (err) => {
      //   if (!err) {
      //     this.stepManager.showErrorStepAlert("Документ не был подписан");
      //   } else {
      //     this.stepManager.showErrorStepAlert(err);
      //   }
      // });
    } else {
      this.confirmService
        .confirmDocument(this.store.value.application.finalRequestId + '')
        .pipe(untilDestroyed(this))
        .subscribe(
          () => {
            void this.navigationService.push('/new-product/credit/select-account', { replaceUrl: true });
            // this.stepManager.changeStep(Steps.selectAccount);
          },
          (error) => {
            const message = error.message || error.description || error || 'Неизвестная ошибка!';
            this.alertService.showInfoAlert(message);
            this.stepManager.setStepLoaded(true);
          }
        );
    }
  }

  showBlank() {
    this.stepManager.setStepLoaded(false);
    const { finalRequestId } = this.store.value.application;
    this.printDocumentService.getPrintDocument(finalRequestId).subscribe(
      (res) => {
        this.stepManager.setStepLoaded(true);
        const date = Date.now();
        this.fileUtilService.extractAttachment(res, `final-anketa-${date}.pdf`, 'application/pdf');
      },
      (error) => {
        const message = error.message || error.description || error || 'Неизвестная ошибка!';
        this.alertService.showInfoAlert(message);
        this.stepManager.setStepLoaded(true);
      }
    );
  }

  private initStepForFirstSending() {
    const userPersonalForm$ = this.store.hasUserPersonalFormData
      ? of({
          form: this.store.value.personalData.userFormGroup,
          fields: this.store.value.personalData.userFormField
        })
      : this.personalDataFormService.getCheckPersonalDataForm();
    const additionalUserPersonalForm$ = this.store.hasAdditionalUserPersonalFormData
      ? of({
          form: this.store.value.personalData.additionalFormGroup,
          fields: this.store.value.personalData.additionalFormField
        })
      : this.personalDataFormService.getAdditionalPersonalDataForm();

    forkJoin(
      this.onlineCreditService.checkExistCredits(this.store.value.application.id),
      userPersonalForm$,
      additionalUserPersonalForm$
    )
      .pipe(untilDestroyed(this))
      .subscribe(([application, userForm, additionalForm]) => {
        this.store.updateApplication(application);
        this.userFormGroup = userForm.form;
        this.userFormFiled = userForm.fields;
        this.additionalFormField = additionalForm.fields;
        this.additionalFormGroup = additionalForm.form;

        this.stepManager.loaderTitle = `<h4 style="margin: 0">Подготовка анкеты</h4>
      Это может занять до 5 минут. Пожалуйста, подождите...`;
        this.groupCredits$ = this.store.activeCredits$;
        this.stepManager.setStepLoaded(true);
      });
  }

  private initStepForFinalSending() {
    this.personalDataFormService
      .getAdditionalPersonalDataForm()
      .pipe(untilDestroyed(this))
      .subscribe((res: any) => {
        this.additionalFormField = res.fields;
        this.additionalFormGroup = res.form;
        this.additionalFormField.address.find((item) => item.controlName === 'snils').isHidden = false;
        this.groupCredits$ = this.store.activeCredits$;
        this.stepManager.setStepLoaded(true);
      });
  }

  goBack() {
    this.stepManager.setStepLoaded(false);
    void this.navigationService.push('/new-product/credit/personal-data', { replaceUrl: true });
    // this.stepManager.changeStep(Steps.personalData);
  }

  abort() {
    this.stepManager.showAbortAlert();
  }
}
